import React, { useCallback, useMemo, type PropsWithChildren, type SyntheticEvent } from 'react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import startWithTemplateSpotlightImage from './assets/start-with-template-spotlight.png';
import { useDelayedEligibility } from './common/useDelayedEligibility.tsx';
import { START_WITH_TEMPLATE_SPOTLIGHT } from './controllers/constants.tsx';
import { useOnboardingFlowsActions } from './controllers/index.tsx';
import { useProductUsageStartTimestamp } from './controllers/selectors/product-usage-start-timestamp-hooks.tsx';
import {
	OnboardingSpotlightFeatureIds,
	PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	PO_HEADLESS_PLACEMENT_ID,
} from './onboardingConstants.tsx';
import { usePolarisHeadlessSpotlightMessage } from './usePolarisHeadlessSpotlightMessage.tsx';
import { checkIfOlderThanNDays } from './utils/checkIfOlderThanNDays.tsx';
import { ConditionalSpotlightTarget } from './ui/ConditionalSpotlightTarget.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';

const spotlightHeading = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.heading',
	defaultMessage: 'Start with a template',
	description: 'Heading for the Start with a template spotlight',
});

const spotlightDescription = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.description',
	defaultMessage: 'Create a view based on popular prioritization frameworks.',
	description: 'Description for the Start with a template spotlight',
});

const spotlightTryItNowButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now',
	defaultMessage: 'Try it now',
	description: 'Primary action button for the Start with a template spotlight',
});

const spotlightDismissButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.start-with-template-spotlight.dismiss',
	defaultMessage: 'Dismiss',
	description: 'Secondary dismiss button for the Start with a template spotlight',
});

const DISMISS_CLICK_IDENTIFIER_BY_ACTION = {
	tryItNow: 'try-it-now',
	dismiss: 'dismiss',
	close: 'close',
} as const;
const PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS = 3000;

type StartWithTemplateSpotlightProps = PropsWithChildren<{
	onTryItNow?: () => void;
	spotlightTargetNode?: HTMLElement | null;
	isDelayEligible?: boolean;
}>;

export const StartWithTemplateSpotlight = ({
	children,
	onTryItNow,
	spotlightTargetNode,
	isDelayEligible = true,
}: StartWithTemplateSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { loadProductUsageStartTimestamp } = useOnboardingFlowsActions();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();
	const { isPoMessageVisible, dismiss, renderJiraSpotlight } = usePolarisHeadlessSpotlightMessage({
		placementId: PO_HEADLESS_PLACEMENT_ID,
		messageTemplateId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
		featureId: OnboardingSpotlightFeatureIds.START_WITH_TEMPLATE,
		clickIdentifiers: {
			dismiss: 'close',
			primary: 'try-it-now',
		},
	});

	useEffectWithoutConcurrency(() => {
		loadProductUsageStartTimestamp();
	}, [loadProductUsageStartTimestamp]);

	const isNewCustomer =
		Boolean(productUsageStartTimestamp) && !checkIfOlderThanNDays(productUsageStartTimestamp, 7);
	const isSpotlightEligible = isDelayEligible && isNewCustomer && isPoMessageVisible;
	const isSpotlightVisible = useDelayedEligibility({
		isEligible: isSpotlightEligible,
		delayMs: PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS,
	});
	const dismissSpotlight = useCallback(
		(clickIdentifier: 'dismiss' | 'try-it-now' | 'close') => {
			dismiss(clickIdentifier === 'try-it-now' ? 'primary' : 'dismiss');
		},
		[dismiss],
	);

	const handleTryItNow = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startWithTemplateSpotlightTryItNow');
			dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.tryItNow);
			onTryItNow?.();
		},
		[onTryItNow, dismissSpotlight],
	);

	const handleDismiss = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startWithTemplateSpotlightDismiss');
			dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.dismiss);
		},
		[dismissSpotlight],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloseButtonDismiss = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({ action: 'dismissed', actionSubject: 'jpdSpotlight' }), {
			activeSpotlight: 'startWithTemplateSpotlight',
		});
		dismissSpotlight(DISMISS_CLICK_IDENTIFIER_BY_ACTION.close);
	}, [createAnalyticsEvent, dismissSpotlight]);

	const actions = useMemo(() => {
		if (
			!fg('post-office_enable_jpd-juno_headless-spotlights') &&
			!fg('pol-21470-juno-spotlights-migration')
		) {
			return [
				{
					onClick: handleTryItNow,
					text: formatMessage(spotlightTryItNowButton),
					ctaType: 'primary' as const,
					testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now-button',
				},
				{
					onClick: handleDismiss,
					text: formatMessage(spotlightDismissButton),
					ctaType: 'secondary' as const,
					appearance: 'subtle' as const,
					testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.dismiss-button',
				},
			];
		}

		return [
			{
				onClick: handleTryItNow,
				text: formatMessage(spotlightTryItNowButton),
				ctaType: 'primary' as const,
				testId: 'polaris.lib-onboarding-flows.start-with-template-spotlight.try-it-now-button',
			},
		];
	}, [handleDismiss, handleTryItNow, formatMessage]);

	if (!isSpotlightVisible) {
		return <>{children}</>;
	}

	const spotlightContent = renderJiraSpotlight({
		actions,
		dialogPlacement: 'bottom left',
		target: START_WITH_TEMPLATE_SPOTLIGHT,
		targetNode: spotlightTargetNode ?? undefined,
		heading: formatMessage(spotlightHeading),
		testId: 'lib-onboarding-flows.start-with-template-spotlight',
		image: startWithTemplateSpotlightImage,
		shouldWatchTarget: true,
		shouldObserveRootForTarget: true,
		onDismiss:
			fg('post-office_enable_jpd-juno_headless-spotlights') ||
			fg('pol-21470-juno-spotlights-migration')
				? onCloseButtonDismiss
				: undefined,
		children: formatMessage(spotlightDescription),
		fallbackMessageId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	});

	return (
		<>
			<ConditionalSpotlightTarget name={START_WITH_TEMPLATE_SPOTLIGHT}>
				{children}
			</ConditionalSpotlightTarget>
			{spotlightContent}
		</>
	);
};
